/**
 * Конфигурация статусов заказов для отображения
 *
 * Централизованная конфигурация всех возможных статусов заказа и оплаты
 * с их визуальными представлениями и текстовыми описаниями
 */

import { ORDER_STATUSES, PAYMENT_STATUSES } from '@shared/config'
import type { OrderStatus, PaymentStatus } from '@shared/config'

/**
 * Интерфейс конфигурации статуса
 */
export interface StatusConfig {
  /** CSS класс для бейджа */
  badgeClass: string
  /** Текстовое сообщение на русском */
  message: string
  /** Приоритет отображения (меньше = выше приоритет) */
  priority: number
  /** Иконка (опционально) */
  icon?: string
}

/**
 * Конфигурация для статусов заказа (ORDER_STATUSES)
 * Приоритет: 10-19
 *
 * Note: Поддерживаем все возможные статусы из типов OrderStatus
 */
export const ORDER_STATUS_CONFIG: Partial<Record<OrderStatus, StatusConfig>> = {
  [ORDER_STATUSES.PENDING]: {
    badgeClass: 'order-widget__badge--pending',
    message: 'в обработке',
    priority: 10,
    icon: 'i-heroicons-clock',
  },
  [ORDER_STATUSES.CANCELLED]: {
    badgeClass: 'order-widget__badge--failed',
    message: 'отменён',
    priority: 11,
    icon: 'i-heroicons-x-circle',
  },
  [ORDER_STATUSES.CONFIRMED]: {
    badgeClass: 'order-widget__badge--confirmed',
    message: 'оплачен',
    priority: 12,
    icon: 'i-heroicons-check-circle',
  },
  [ORDER_STATUSES.PREPARING]: {
    badgeClass: 'order-widget__badge--preparing',
    message: 'готовится',
    priority: 13,
    icon: 'i-heroicons-clock',
  },
  [ORDER_STATUSES.READY]: {
    badgeClass: 'order-widget__badge--ready',
    message: 'готов',
    priority: 14,
    icon: 'i-heroicons-check-badge',
  },
  [ORDER_STATUSES.DELIVERY]: {
    badgeClass: 'order-widget__badge--delivery',
    message: 'в доставке',
    priority: 15,
    icon: 'i-heroicons-truck',
  },
  [ORDER_STATUSES.DELIVERED]: {
    badgeClass: 'order-widget__badge--delivered',
    message: 'получен',
    priority: 16,
    icon: 'i-heroicons-hand-thumb-up',
  },
  [ORDER_STATUSES.COMPLETED]: {
    badgeClass: 'order-widget__badge--delivered',
    message: 'выполнен',
    priority: 17,
    icon: 'i-heroicons-check-badge',
  },
} as const

/**
 * Конфигурация для статусов оплаты (PAYMENT_STATUSES)
 * Приоритет для критичных: 1-9
 * Приоритет для обычных: 20-29
 */
export const PAYMENT_STATUS_CONFIG: Record<PaymentStatus, StatusConfig> = {
  [PAYMENT_STATUSES.FAILED]: {
    badgeClass: 'order-widget__badge--failed',
    message: 'оплата не прошла',
    priority: 1,
    icon: 'i-heroicons-exclamation-circle',
  },
  [PAYMENT_STATUSES.CANCELLED]: {
    badgeClass: 'order-widget__badge--cancelled',
    message: 'платёж отменён',
    priority: 2,
    icon: 'i-heroicons-x-circle',
  },
  [PAYMENT_STATUSES.REFUNDED]: {
    badgeClass: 'order-widget__badge--refunded',
    message: 'возврат средств',
    priority: 3,
    icon: 'i-heroicons-arrow-uturn-left',
  },
  [PAYMENT_STATUSES.PENDING]: {
    badgeClass: 'order-widget__badge--pending',
    message: 'обработка платежа',
    priority: 20,
    icon: 'i-heroicons-clock',
  },
  [PAYMENT_STATUSES.WAITING]: {
    badgeClass: 'order-widget__badge--pending',
    message: 'ожидает оплаты',
    priority: 21,
    icon: 'i-heroicons-clock',
  },
  [PAYMENT_STATUSES.SUCCEEDED]: {
    badgeClass: 'order-widget__badge--confirmed',
    message: 'оплачен',
    priority: 22,
    icon: 'i-heroicons-check-circle',
  },
  [PAYMENT_STATUSES.PAID]: {
    badgeClass: 'order-widget__badge--confirmed',
    message: 'оплачен',
    priority: 22,
    icon: 'i-heroicons-check-circle',
  },
} as const

/**
 * Конфигурация по умолчанию
 */
export const DEFAULT_STATUS_CONFIG: StatusConfig = {
  badgeClass: 'order-widget__badge--default',
  message: 'обрабатывается',
  priority: 99,
  icon: 'i-heroicons-ellipsis-horizontal-circle',
}

/**
 * Статусы оплаты, для которых НЕ нужно показывать таймер
 */
export const TIMER_EXCLUDED_PAYMENT_STATUSES = new Set<PaymentStatus>([
  PAYMENT_STATUSES.PAID,
  PAYMENT_STATUSES.SUCCEEDED,
  PAYMENT_STATUSES.CANCELLED,
  PAYMENT_STATUSES.REFUNDED,
  PAYMENT_STATUSES.FAILED,
])
